<script lang="ts">
/**
 @module routes/+layout
 @description SwiftUI-inspired app shell providing the global UI framework.
 On desktop, presents a macOS-style NavigationSplitView with persistent sidebar.
 On mobile, provides an iOS-style UITabBar at the bottom with the sidebar drawer
 accessible via the "More" tab.
 Uses Liquid Glass material throughout (backdrop-blur, translucency) with Nord color scheme.
 Applies a dark mode default via ModeWatcher.

 Wraps all pages with global UI shell elements including the sidebar navigation,
 top navigation bar, animated particle background, page transition animations,
 toast notifications, cookie consent banner, and the site footer.

 Key responsibilities:
 - Provides the global `SidebarProvider` context for the `AppSidebar` drawer
 - Renders the interactive `ClickSpark` effect layer on user clicks
 - Applies fly/fade page transitions keyed on `page.url.pathname`
 - Mounts global singletons: `Toaster`, `RecommendationToasts`, `CookieConsent`, `Footer`
 - Renders the animated `Particles` background with 400 particles

 @property {Snippet} children - The child page content rendered inside the main area

 @see {@link $lib/components/AppSidebar.svelte} for sidebar navigation
 @see {@link $lib/components/NavBar.svelte} for desktop navigation trigger
 @see {@link $lib/components/TabBar.svelte} for iOS-style mobile tab bar
 @see {@link $lib/components/Footer.svelte} for site footer
 @see {@link $lib/components/cookie/CookieConsent.svelte} for GDPR cookie consent
*/
import "./layout.css";
import { SidebarProvider, SidebarInset } from "$lib/components/ui/sidebar";
import AppSidebar from "$lib/components/AppSidebar.svelte";
import NavBar from "$lib/components/NavBar.svelte";
import TabBar from "$lib/components/TabBar.svelte";
import ClickSpark from "$lib/components/ClickSpark.svelte";
import Particles from "$lib/components/particles";
import Footer from "$lib/components/Footer.svelte";
import CommandMenu from "$lib/components/command-menu/CommandMenu.svelte";
import RecommendationToasts from "$lib/components/RecommendationToasts.svelte";
import CookieConsent from "$lib/components/cookie/CookieConsent.svelte";
import ReloadPrompt from "$lib/components/ReloadPrompt.svelte";
import { Toaster } from "$lib/components/ui/sonner/index";
import { ModeWatcher } from "mode-watcher";
import type { Snippet } from "svelte";
import { page } from "$app/state";
import { afterNavigate } from "$app/navigation";
import { fly, fade } from "svelte/transition";
import { cubicOut, cubicIn } from "svelte/easing";
import { cn } from "$lib/utils";
import { IsMobile } from "$lib/hooks/is-mobile.svelte";
import { IsTablet } from "$lib/hooks/is-tablet.svelte";
import { pwaAssetsHead } from "virtual:pwa-assets/head";
import { injectSpeedInsights } from "@vercel/speed-insights/sveltekit";
import { browser, dev } from "$app/env";
import { injectAnalytics } from "@vercel/analytics/sveltekit";
import GoogleAnalytics from "$lib/components/GoogleAnalytics.svelte";
import FingerprintTracker from "$lib/components/FingerprintTracker.svelte";
import ConsentWrapper from "$lib/components/cookie/ConsentWrapper.svelte";

const isMobile = new IsMobile();
const isTablet = new IsTablet();

let particleCount = $state(400);

$effect(() => {
  if (isMobile.current) particleCount = 50;
  else if (isTablet.current) particleCount = 200;
  else particleCount = 400;
});

if (browser) {
  try {
    injectAnalytics({ mode: dev ? "development" : "production" });
  } catch (e) {
    // Vercel analytics uses deprecated $app/stores internally in Svelte 5
  }
  try {
    injectSpeedInsights();
  } catch (e) {
    // Vercel speed insights uses deprecated $app/stores internally in Svelte 5
  }
}

let { children }: { children: Snippet } = $props();
let appElement: HTMLElement | undefined;

afterNavigate(() => {
  if (appElement) {
    appElement.scrollTop = 0;
    appElement.scrollLeft = 0;
  }
});
</script>

<GoogleAnalytics />
<ConsentWrapper category="tracking">
  <FingerprintTracker />
</ConsentWrapper>
<svelte:head>
  <meta charset="utf-8" />
  <link
    rel="manifest"
    href="/manifest.webmanifest"
    data-sveltekit-preload-code
    data-sveltekit-preload-data
  />
  <link
    rel="robots"
    href="/robots.txt"
    data-sveltekit-preload-code
    data-sveltekit-preload-data
  />
  <link
    rel="sitemap"
    href="/sitemap.xml"
    data-sveltekit-preload-code
    data-sveltekit-preload-data
  />
  <link
    rel="llm"
    href="/llm.txt"
    data-sveltekit-preload-code
    data-sveltekit-preload-data
  />
  {#if pwaAssetsHead.themeColor}
    <meta name="theme-color" content={pwaAssetsHead.themeColor.content} />
  {/if}
  {#each pwaAssetsHead.links as link}
    <link
      rel={link.rel}
      href={("/pictures" + link.href).replace("/pictures./", "/pictures/")}
      media={link.media}
      sizes={link.sizes}
      id={link.id}
      type={link.type}
      data-sveltekit-preload-code
      data-sveltekit-preload-data
    />
  {/each}
</svelte:head>

<ModeWatcher defaultMode="dark" />

<ClickSpark
  sparkColor="#ECEFF4"
  sparkSize={10}
  sparkRadius={15}
  sparkCount={8}
  duration={400}
>
  <SidebarProvider
    style="--sidebar-width: var(--appsidebar-width, 20rem); --sidebar-width-mobile: var(--appsidebar-width-mobile, 22rem);"
  >
    <AppSidebar />
    <SidebarInset class="bg-transparent overflow-hidden">
      <Particles quantity={particleCount} contained />
      <div
        bind:this={appElement}
        class={cn(
          "app dark isolate relative z-10 flex flex-col h-svh w-full overflow-y-auto scroll-smooth pb-[calc(4.5rem+env(safe-area-inset-bottom,0px))] md:pb-0",
        )}
      >
        <NavBar />

        <main class={cn("flex-1 w-full max-w-full relative")}>
          {@render children()}
        </main>
        {#if browser}
          <Toaster
            theme="dark"
            class={cn("rounded-sm my-glass shadow-lg print:hidden no-print")}
            expand={true}
          />
          <RecommendationToasts />
          <CookieConsent />
          <ReloadPrompt />
          <CommandMenu />
        {/if}
        <Footer />
        <TabBar />
      </div>
    </SidebarInset>
  </SidebarProvider>
</ClickSpark>
